import React, { useState } from "react";
import {
  ArrowRight,
  ShieldCheck,
  GraduationCap,
  LogIn,
  Sparkles,
  Brain,
  Quote,
} from "lucide-react";
import LeadDialog from "@/components/RegisterDialog";
import { LANDING } from "@/constants/testIds";

/* -------------------------------------------------------------------------- */
/*  Layout primitives — strict adherence to the brief:                         */
/*   · Section: alternating bgs (terziario / quaternario / primario)          */
/*   · Generous vertical padding ≥ 96px desktop / 80px mobile                  */
/*   · Reading column max 800px for all long-form copy                         */
/*   · No absolute positioning that overflows containers                       */
/* -------------------------------------------------------------------------- */

const Section = ({ id, bg = "var(--np-tertiary)", color, testId, children, className = "" }) => (
  <section
    id={id}
    data-testid={testId}
    className={`relative ${className}`}
    style={{
      background: bg,
      color: color,
      paddingTop: "clamp(96px, 12vw, 168px)",
      paddingBottom: "clamp(96px, 12vw, 168px)",
    }}
  >
    {children}
  </section>
);

const Reading = ({ children, className = "" }) => (
  <div className={`mx-auto px-6 ${className}`} style={{ maxWidth: 700 }}>
    {children}
  </div>
);

const Wide = ({ children, className = "" }) => (
  <div className={`mx-auto px-6 ${className}`} style={{ maxWidth: 1100 }}>
    {children}
  </div>
);

const Eyebrow = ({ children, color = "var(--np-secondary)" }) => (
  <p
    className="text-[12px] font-semibold tracking-[0.25em] uppercase mb-4"
    style={{ color }}
  >
    {children}
  </p>
);

const Cta = ({ label = "Guarda gratis la prima lezione", onClick, testId }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testId}
    className="cta-btn"
  >
    {label}
    <ArrowRight size={18} strokeWidth={2.4} />
  </button>
);

/* -------------------------------------------------------------------------- */
/*  Page                                                                      */
/* -------------------------------------------------------------------------- */

export default function LandingPage() {
  const [open, setOpen] = useState(false);
  const openModal = () => setOpen(true);

  return (
    <main className="App">
      {/* ============================ HEADER ============================ */}
      <header
        data-testid="site-header"
        className="w-full border-b"
        style={{ background: "var(--np-quaternary)", borderColor: "rgba(26,42,58,0.06)", paddingTop: "10px", paddingBottom: "10px" }}
      >
        <div className="mx-auto px-6 md:px-10 flex items-center justify-between gap-4" style={{ maxWidth: 1280 }}>
          <a href="/" aria-label="Neuroscienza Pratica — Figlio a Carico" className="inline-flex items-center">
            <img
              src="https://customer-assets.emergentagent.com/job_ciclo-responsabilita/artifacts/07tez42a_11%20%2824%29.png"
              alt="Neuroscienza Pratica — Figlio a Carico"
              className="block"
              style={{
                width: "clamp(260px, 28vw, 440px)",
                aspectRatio: "4 / 1",
                objectFit: "cover",
                objectPosition: "center",
              }}
            />
          </a>
          <div className="flex items-center gap-2 sm:gap-3 md:gap-5">
            {/* Pulsante "Accedi" — sempre visibile (mobile + desktop) per chi
                è già registrato e vuole rientrare senza rifare il flusso.
                Stilizzato come pillola outline, in contrasto con il CTA
                primario "Guarda gratis" che resta il focus della header. */}
            <a
              href="/login"
              data-testid="nav-login-link"
              className="inline-flex items-center gap-1.5 px-3.5 sm:px-4 py-2 sm:py-2.5 rounded-full text-sm font-semibold border-2 transition hover:brightness-95 whitespace-nowrap"
              style={{
                color: "var(--np-primary)",
                borderColor: "var(--np-primary)",
                background: "transparent",
              }}
            >
              <LogIn size={14} strokeWidth={2.4} />
              Accedi
            </a>
            <button
              type="button"
              onClick={openModal}
              data-testid="cta-header"
              className="hidden md:inline-flex items-center gap-1.5 px-5 lg:px-6 py-2.5 lg:py-3 rounded-full text-sm lg:text-base font-semibold text-white transition hover:brightness-110 whitespace-nowrap"
              style={{ background: "var(--np-secondary)" }}
            >
              Guarda gratis la prima lezione
            </button>
          </div>
        </div>
      </header>

      {/* ============================ SECTION 1 — HERO ============================ */}
      <Section
        testId={LANDING.hero}
        bg="var(--np-tertiary)"
        className="text-center"
      >
        <Reading className="text-center">
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-white border border-[#dbe3ec] mb-8">
            <Sparkles size={14} style={{ color: "var(--np-secondary)" }} />
            <span
              className="text-[12px] font-semibold tracking-wider uppercase"
              style={{ color: "var(--np-primary)" }}
            >
              Neuroscienza Pratica · Figlio a Carico
            </span>
          </div>

          <h1
            className="font-serif-display text-4xl sm:text-5xl lg:text-[58px] leading-[1.08] tracking-tight"
            style={{ color: "var(--np-primary)" }}
          >
            Smetti di chiedere agli altri
            <span className="block italic mt-2" style={{ color: "var(--np-secondary)" }}>
              come aiutare
            </span>
            <span className="block mt-2">tuo figlio</span>
          </h1>

          <p
            className="mt-9 text-lg md:text-xl leading-[1.75]"
            style={{ color: "#2a3a4a" }}
          >
            Impara come <strong style={{ color: "var(--np-primary)" }}>comunicare correttamente</strong> con
            tuo figlio per aiutarlo a{" "}
            <strong style={{ color: "var(--np-primary)" }}>diventare finalmente un adulto</strong> grazie a{" "}
            <span className="italic" style={{ fontFamily: "Merriweather, serif" }}>
              Neuroscienza Pratica — Figlio a Carico.
            </span>
          </p>

          <p
            className="mt-4 text-sm md:text-base font-medium"
            style={{ color: "var(--np-muted)" }}
          >
            (garanzia 60 giorni, soddisfatto o rimborsato)
          </p>

          <div className="mt-10 flex flex-col items-center gap-4">
            <Cta onClick={openModal} testId={LANDING.ctaHero} />
            <div
              className="flex items-center gap-2 text-sm"
              style={{ color: "var(--np-muted)" }}
            >
              <ShieldCheck size={16} style={{ color: "var(--np-secondary)" }} />
              <span>60 giorni di garanzia totale</span>
            </div>
          </div>
        </Reading>
      </Section>

      {/* ============================ SECTION 2 — LE HAI PROVATE TUTTE ============================ */}
      <Section
        testId={LANDING.echoGrid}
        bg="var(--np-quaternary)"
      >
        <Wide>
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-14 items-center">
            {/* LEFT — psicologa image */}
            <div className="md:col-span-5 order-2 md:order-1">
              <div className="relative aspect-square rounded-3xl overflow-hidden shadow-[0_30px_80px_-30px_rgba(26,42,58,0.45)]">
                <img
                  src="https://customer-assets.emergentagent.com/job_ciclo-responsabilita/artifacts/z1jytx82_11.jpg"
                  alt="Sessione con lo psicologo — un genitore che cerca aiuto fuori dalle mura di casa"
                  loading="lazy"
                  className="absolute inset-0 w-full h-full object-cover"
                />
              </div>
            </div>

            {/* RIGHT — copy */}
            <div className="md:col-span-7 order-1 md:order-2">
              <Eyebrow>Hai già tentato di tutto</Eyebrow>
              <h2
                className="text-3xl md:text-[42px] leading-[1.12] tracking-tight"
                style={{ color: "var(--np-primary)", fontFamily: "Merriweather, serif" }}
              >
                Le hai provate <span className="italic" style={{ color: "var(--np-secondary)" }}>tutte…</span>
              </h2>

              <div
                className="mt-7 space-y-5 text-[17px] md:text-lg leading-[1.75]"
                style={{ color: "#2a3a4a" }}
              >
                <p>
                  Hai provato a farlo parlare con lo psicologo,
                  <span className="block">
                    ma lui o <strong style={{ color: "var(--np-primary)" }}>li respinge</strong> o{" "}
                    <strong style={{ color: "var(--np-primary)" }}>li rigira</strong>.
                  </span>
                </p>
                <p>
                  Hai provato a confrontarti con le tue amiche…{" "}
                  <em className="not-italic font-semibold" style={{ color: "var(--np-secondary)" }}>
                    ma loro che ne sanno?
                  </em>
                </p>
                <p>
                  Hai provato a confrontarti con lo psicologo, ma{" "}
                  <strong style={{ color: "var(--np-primary)" }}>lui non conosce tuo figlio</strong>: non ci
                  parla, non conosce la tua storia, non conosce il rapporto che hai con lui.
                </p>
              </div>
            </div>
          </div>
        </Wide>
      </Section>

      {/* ============================ SECTION 3 — MAGARI NON È COLPA TUA ============================ */}
      <Section
        testId={LANDING.darkAgitation}
        bg="var(--np-primary)"
        color="#ffffff"
      >
        <Reading className="text-center">
          <div className="space-y-10 md:space-y-12">
            <p
              className="text-xl md:text-2xl leading-[1.6]"
              style={{ fontFamily: "Merriweather, serif" }}
            >
              Magari <span className="italic" style={{ color: "var(--np-secondary)" }}>non è colpa tua</span>,
              <span className="block mt-3">
                ma tu sei{" "}
                <strong style={{ color: "var(--np-secondary)" }}>
                  l&apos;unica persona al mondo
                </strong>{" "}
                che può aiutarlo.
              </span>
            </p>

            {/* Cinematic image — solitude */}
            <figure className="my-4 md:my-6">
              <img
                src="https://customer-assets.emergentagent.com/job_ciclo-responsabilita/artifacts/ndukv68z_11%20%282%29.jpg"
                alt="Una madre sola, davanti al muro delle proprie domande"
                loading="lazy"
                className="block w-full h-auto rounded-xl shadow-[0_30px_80px_-30px_rgba(0,0,0,0.7)]"
                style={{ aspectRatio: "1 / 1", objectFit: "cover" }}
              />
            </figure>

            <p
              className="text-lg md:text-xl leading-[1.7] opacity-90"
              style={{ fontFamily: "Merriweather, serif" }}
            >
              E l&apos;unico modo che hai per aiutarlo è <span style={{ color: "var(--np-secondary)" }}>acquisire gli strumenti giusti</span> per comprendere questa situazione.
            </p>
          </div>
        </Reading>
      </Section>

      {/* ============================ SECTION 4 — SOLUTION CARD ============================ */}
      <Section
        testId={LANDING.featureCard}
        bg="var(--np-tertiary)"
      >
        <Reading>
          <div className="bg-white rounded-3xl shadow-[0_30px_80px_-30px_rgba(26,42,58,0.35)] border border-[#e3eaf2] p-8 md:p-14 text-center">
            <div
              className="w-14 h-14 mx-auto rounded-2xl flex items-center justify-center mb-6"
              style={{ background: "var(--np-quaternary)" }}
            >
              <Brain size={26} style={{ color: "var(--np-secondary)" }} />
            </div>

            <Eyebrow>La soluzione</Eyebrow>

            <div className="-mt-2 mb-6 flex justify-center">
              <img
                src="https://customer-assets.emergentagent.com/job_ciclo-responsabilita/artifacts/07tez42a_11%20%2824%29.png"
                alt="Neuroscienza Pratica — Figlio a Carico"
                className="block w-full sm:w-4/5 lg:w-3/4"
                style={{
                  maxWidth: 880,
                  aspectRatio: "4 / 1",
                  objectFit: "cover",
                  objectPosition: "center",
                }}
              />
            </div>

            <h2 className="sr-only">Neuroscienza Pratica — Figlio a Carico</h2>

            <p
              className="mt-7 text-lg leading-[1.75]"
              style={{ color: "#2a3a4a" }}
            >
              È per questo che nasce <strong style={{ color: "var(--np-primary)" }}>Neuroscienza Pratica — Figlio a Carico</strong>, il <strong>primo corso al mondo</strong> di Neuroscienza applicata alla vita quotidiana, <strong>garantito al 100%</strong>.
            </p>

            <p
              className="mt-5 text-lg leading-[1.75]"
              style={{ color: "#2a3a4a" }}
            >
              Dove troverai sia <em>spiegazioni teoriche</em> che <em>esempi pratici applicati al tuo caso</em>, grazie ai quali riuscirai a capire come <strong style={{ color: "var(--np-secondary)" }}>sbloccare il tuo rapporto con tuo figlio</strong> e ad aiutarlo a <strong style={{ color: "var(--np-primary)" }}>diventare un Adulto.</strong>
            </p>

            <div className="mt-10 pt-8 border-t border-[#eef2f7] flex flex-col items-center gap-4">
              <Cta onClick={openModal} testId={LANDING.ctaSolution} />
              <div className="flex items-center gap-2 text-sm" style={{ color: "var(--np-muted)" }}>
                <ShieldCheck size={16} style={{ color: "var(--np-secondary)" }} />
                <span>Garanzia 60 giorni · Soddisfatto o rimborsato</span>
              </div>
            </div>
          </div>
        </Reading>
      </Section>

      {/* ============================ SECTION 5 — AUTHORITY / BIO ============================ */}
      <Section
        testId={LANDING.bioSection}
        bg="var(--np-quaternary)"
      >
        <Wide>
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-14 items-start">
            {/* LEFT — photo */}
            <div className="md:col-span-5">
              <div className="relative aspect-[4/5] rounded-3xl overflow-hidden shadow-[0_30px_80px_-30px_rgba(26,42,58,0.45)]">
                <img
                  src="https://customer-assets.emergentagent.com/job_ciclo-responsabilita/artifacts/hzm2lgqx_ChatGPT%20Image%2012%20giu%202026%2C%2014_37_18.png"
                  alt="Alessandro Fulgheri — titolare di BeeToo Agency Srl, certificato in Neuroscienze a Harvard"
                  loading="lazy"
                  className="absolute inset-0 w-full h-full object-cover"
                />
                <div
                  className="absolute inset-0 pointer-events-none mix-blend-soft-light opacity-40"
                  style={{ background: "var(--np-tension)" }}
                  aria-hidden
                />
              </div>
            </div>

            {/* RIGHT — copy (within an 800px-equivalent column) */}
            <div className="md:col-span-7">
              <Eyebrow>Chi sono io per proporti questo</Eyebrow>
              <h2
                className="text-3xl md:text-[42px] leading-[1.12] tracking-tight"
                style={{ color: "var(--np-primary)", fontFamily: "Merriweather, serif" }}
              >
                Sono <span className="italic">Alessandro Fulgheri,</span>{" "}
                <span className="block">titolare di BeeToo Agency S.r.l.</span>
              </h2>

              <div
                className="mt-7 space-y-5 text-[17px] md:text-lg leading-[1.75]"
                style={{ color: "#2a3a4a" }}
              >
                <p>
                  Per eccellere con la mia agenzia di Marketing e sviluppare un sistema che ci permettesse di
                  vincere in mercati ultra competitivi,{" "}
                  <strong style={{ color: "var(--np-primary)" }}>
                    ho dovuto studiare ed applicare innumerevoli scoperte neuroscientifiche
                  </strong>{" "}
                  a svariati mercati.
                </p>
                <p>
                  E questo mi ha permesso di vedere{" "}
                  <strong>l&apos;applicazione delle neuroscienze anche nella vita di tutti i giorni</strong>.
                </p>
              </div>

              {/* Harvard pill — flex row, vertical center, NO absolute positioning */}
              <div
                className="mt-8 inline-flex items-center gap-4 rounded-2xl border border-[#e3d6c6] bg-white px-5 py-4 shadow-[0_10px_30px_-22px_rgba(26,42,58,0.4)]"
              >
                <div
                  className="w-11 h-11 rounded-full flex items-center justify-center shrink-0"
                  style={{ background: "#A41034" }}
                  aria-hidden
                >
                  <GraduationCap size={22} color="#ffffff" />
                </div>
                <p
                  className="text-[15px] md:text-base leading-[1.55]"
                  style={{ color: "var(--np-primary)" }}
                >
                  Tra le mie fonti di studio c&apos;è anche{" "}
                  <strong style={{ fontFamily: "Merriweather, serif" }}>Harvard</strong>: la prestigiosa scuola
                  statunitense, con la quale ho preso la certificazione in Neuroscienze.
                </p>
              </div>

              <div className="mt-9">
                <Cta onClick={openModal} testId={LANDING.ctaBio} />
              </div>
            </div>
          </div>
        </Wide>
      </Section>

      {/* ============================ SECTION 6 — GUARANTEE ============================ */}
      <Section bg="var(--np-tertiary)">
        <Reading>
          <div
            data-testid={LANDING.trustBox}
            className="bg-white rounded-3xl p-8 md:p-14 text-center"
            style={{ border: "4px solid transparent", backgroundImage: "linear-gradient(#ffffff,#ffffff), var(--np-tension)", backgroundOrigin: "border-box", backgroundClip: "padding-box, border-box" }}
          >
            {/* Shield icon INSIDE the box — relative flow, never overflows */}
            <div
              className="w-20 h-20 md:w-24 md:h-24 mx-auto rounded-2xl flex items-center justify-center mb-7 shadow-xl"
              style={{ background: "var(--np-tension)" }}
              aria-hidden
            >
              <ShieldCheck size={42} color="#ffffff" strokeWidth={2} />
            </div>

            <Eyebrow>La nostra promessa</Eyebrow>

            <h2
              className="text-3xl md:text-5xl leading-[1.1] tracking-tight"
              style={{ color: "var(--np-primary)", fontFamily: "Merriweather, serif" }}
            >
              Soddisfatto o rimborsato
              <span
                className="block italic mt-2"
                style={{ color: "var(--np-secondary)" }}
              >
                a 60 giorni.
              </span>
            </h2>

            <p
              className="mt-7 text-base md:text-lg leading-[1.75]"
              style={{ color: "#2a3a4a" }}
            >
              E proprio perché sono sicuro che questo corso ti aiuterà, ti offro la mia garanzia. Se per
              qualsiasi ragione questo corso non riuscirà ad aiutarti e non sarai soddisfatto del tuo
              acquisto, ti basterà scrivere un&apos;email a{" "}
              <a
                href="mailto:info@beetooagency.com?subject=Richiesta%20di%20Rimborso"
                className="font-semibold underline-offset-4 hover:underline"
                style={{ color: "var(--np-primary)" }}
              >
                info@beetooagency.com
              </a>{" "}
              con oggetto <strong>“Richiesta di Rimborso”</strong>, il tuo nome, cognome, la mail con cui hai
              effettuato l&apos;acquisto e il tuo numero di telefono, e ti restituiremo i soldi entro{" "}
              <strong style={{ color: "var(--np-primary)" }}>5 giorni</strong> dalla richiesta.
            </p>
          </div>
        </Reading>
      </Section>

      {/* ============================ SECTION 7 — ORIGIN STORY (MARILENA) ============================ */}
      <Section
        testId="landing-marilena-story"
        bg="var(--np-primary)"
        color="#ffffff"
        className="relative overflow-hidden"
      >
        {/* Soft radial glow decoration */}
        <div
          aria-hidden
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              "radial-gradient(ellipse 60% 40% at 50% 0%, rgba(213,93,57,0.18), transparent 70%)",
          }}
        />

        <div className="relative" style={{ maxWidth: 880, margin: "0 auto", padding: "0 24px" }}>
          {/* Decorative ornament */}
          <div className="flex justify-center mb-6">
            <div
              className="w-12 h-[2px]"
              style={{ background: "var(--np-secondary)" }}
              aria-hidden
            />
          </div>

          <div className="text-center">
            <Eyebrow color="rgba(255,255,255,0.65)">L&apos;origine di questo corso</Eyebrow>
            <h2
              className="text-3xl md:text-[48px] leading-[1.08] tracking-tight"
              style={{ fontFamily: "Merriweather, serif" }}
            >
              Ma come mai{" "}
              <span className="italic" style={{ color: "var(--np-secondary)" }}>
                tutto questo?
              </span>
            </h2>
          </div>

          {/* Marilena introduction — portrait + letter-like card */}
          <div className="mt-14 grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-8 items-stretch" style={{ maxWidth: 880, margin: "56px auto 0" }}>
            {/* Marilena portrait */}
            <div className="md:col-span-5">
              <div
                className="relative rounded-2xl overflow-hidden h-full"
                style={{
                  aspectRatio: "1 / 1",
                  boxShadow: "0 30px 80px -30px rgba(0,0,0,0.7)",
                  border: "1px solid rgba(255,255,255,0.08)",
                }}
              >
                <img
                  src="https://customer-assets.emergentagent.com/job_ciclo-responsabilita/artifacts/jj85f9mx_11%20%285%29.jpg"
                  alt="Marilena — una madre nella sua disperazione, illuminata da una lampada nella sera"
                  loading="lazy"
                  className="absolute inset-0 w-full h-full object-cover"
                />
                <div
                  aria-hidden
                  className="absolute inset-0 pointer-events-none"
                  style={{
                    background:
                      "linear-gradient(180deg, transparent 50%, rgba(26,42,58,0.5) 100%)",
                  }}
                />
              </div>
            </div>

            {/* Letter-like card */}
            <figure
              className="md:col-span-7 p-7 md:p-9 rounded-2xl text-left relative flex flex-col justify-center"
              style={{
                background: "rgba(255,255,255,0.04)",
                border: "1px solid rgba(255,255,255,0.10)",
                backdropFilter: "blur(6px)",
              }}
            >
              <Quote
                size={42}
                strokeWidth={1.5}
                style={{
                  color: "var(--np-secondary)",
                  opacity: 0.35,
                  position: "absolute",
                  top: 18,
                  left: 22,
                }}
                aria-hidden
              />
              <p
                className="text-lg md:text-xl leading-[1.8] pl-10"
                style={{ fontFamily: "Merriweather, serif" }}
              >
                <span
                  className="block text-[12px] tracking-[0.3em] uppercase mb-3"
                  style={{ color: "var(--np-secondary)" }}
                >
                  Una storia vera
                </span>
                <strong
                  className="font-semibold"
                  style={{ color: "#ffffff", borderBottom: "2px solid var(--np-secondary)", paddingBottom: 2 }}
                >
                  Marilena
                </strong>
                , una mia dipendente — una persona per bene come poche —{" "}
                <em className="not-italic" style={{ color: "rgba(255,255,255,0.85)" }}>
                  veniva a lavoro disperata.
                </em>
              </p>
            </figure>
          </div>

          {/* Marilena quotes — vintage letter feel */}
          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-5">
            {[
              { text: "È colpa mia", rotate: -1.2 },
              { text: "Gli ho rovinato l'infanzia", rotate: 0.8 },
              { text: "Sono un genitore di merda", rotate: -0.6 },
            ].map(({ text, rotate }) => (
              <div
                key={text}
                className="relative rounded-xl p-6 md:p-7 text-center transition-transform hover:rotate-0"
                style={{
                  background: "linear-gradient(180deg, rgba(255,255,255,0.06) 0%, rgba(255,255,255,0.025) 100%)",
                  border: "1px solid rgba(255,255,255,0.14)",
                  transform: `rotate(${rotate}deg)`,
                  boxShadow: "0 20px 40px -20px rgba(0,0,0,0.5)",
                  minHeight: 140,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <p
                  className="text-lg md:text-xl italic leading-snug"
                  style={{
                    fontFamily: "Merriweather, serif",
                    color: "#ffffff",
                  }}
                >
                  <span
                    style={{
                      color: "var(--np-secondary)",
                      fontSize: "1.6em",
                      lineHeight: 0,
                      marginRight: 4,
                      verticalAlign: "-0.15em",
                    }}
                    aria-hidden
                  >
                    “
                  </span>
                  {text}
                  <span
                    style={{
                      color: "var(--np-secondary)",
                      fontSize: "1.6em",
                      lineHeight: 0,
                      marginLeft: 4,
                      verticalAlign: "-0.4em",
                    }}
                    aria-hidden
                  >
                    ”
                  </span>
                </p>
              </div>
            ))}
          </div>

          {/* Narrative continuation — left-aligned with subtle accent */}
          <div
            className="mt-16 mx-auto"
            style={{ maxWidth: 640 }}
          >
            <div
              className="pl-6 md:pl-8 space-y-7 text-lg md:text-xl leading-[1.8] text-left"
              style={{
                borderLeft: "2px solid rgba(213,93,57,0.5)",
                color: "rgba(255,255,255,0.92)",
              }}
            >
              <p>
                Ed io… che inizialmente cercavo di{" "}
                <em
                  className="not-italic font-semibold"
                  style={{ color: "var(--np-secondary)" }}
                >
                  tenermi fuori
                </em>{" "}
                da queste dinamiche…
              </p>
              <p
                className="text-xl md:text-2xl"
                style={{ fontFamily: "Merriweather, serif", color: "#ffffff" }}
              >
                non potevo più accettare di vederla soffrire.
              </p>
              <p>
                E dopo aver capito quali fossero{" "}
                <strong style={{ color: "var(--np-secondary)" }}>gli strumenti giusti</strong> da darle per
                risolvere questa situazione, ho deciso di renderli disponibili{" "}
                <strong>a tutti i genitori</strong> che si trovano in dinamiche di questo tipo.
              </p>
            </div>
          </div>

          {/* Final reveal — emphasized box */}
          <div
            className="mt-14 mx-auto p-8 md:p-10 rounded-3xl text-center"
            style={{
              maxWidth: 760,
              background:
                "linear-gradient(135deg, rgba(213,93,57,0.18) 0%, rgba(213,93,57,0.06) 100%)",
              border: "1px solid rgba(213,93,57,0.35)",
            }}
          >
            <p
              className="text-[11px] tracking-[0.35em] uppercase mb-3"
              style={{ color: "var(--np-secondary)" }}
            >
              È così che nasce
            </p>
            <p
              className="text-2xl md:text-[32px] leading-[1.2]"
              style={{ fontFamily: "Merriweather, serif", color: "#ffffff" }}
            >
              <span style={{ color: "var(--np-secondary)" }}>Neuroscienza Pratica</span>
              <span className="block mt-1">— Figlio a Carico.</span>
            </p>
            <p
              className="mt-5 text-base md:text-lg leading-[1.7]"
              style={{ color: "rgba(255,255,255,0.85)" }}
            >
              Il <strong>primo corso al mondo</strong> di Neuroscienza applicata alla vita quotidiana, <strong>garantito al 100%</strong>.
            </p>
          </div>

          <div className="mt-12 flex flex-col items-center gap-4">
            <Cta onClick={openModal} testId={LANDING.ctaFinal} />
            <div className="flex items-center gap-2 text-sm" style={{ color: "rgba(255,255,255,0.7)" }}>
              <ShieldCheck size={16} style={{ color: "var(--np-secondary)" }} />
              <span>Garanzia 60 giorni · Soddisfatto o rimborsato</span>
            </div>
          </div>
        </div>
      </Section>

      {/* ============================ SECTION 8 — LEGAL FOOTER ============================ */}
      <Section
        bg="var(--np-primary)"
        color="rgba(255,255,255,0.75)"
        testId={LANDING.legalFooter}
        className="text-center"
      >
        <Reading className="text-center">
          <p
            className="text-[11px] tracking-[0.35em] uppercase mb-5"
            style={{ color: "rgba(255,255,255,0.45)" }}
          >
            Informazioni aziendali
          </p>

          <p
            className="text-xl md:text-2xl"
            style={{ color: "#ffffff", fontFamily: "Merriweather, serif" }}
          >
            BEETOO AGENCY S.R.L.
          </p>
          <p className="text-sm mt-2 tracking-wide">
            Partita IVA: <span className="font-medium" style={{ color: "#ffffff" }}>01275560322</span>
          </p>

          <div
            className="mt-10 pt-7 border-t flex flex-col md:flex-row items-center justify-center gap-2 md:gap-6 text-[13px]"
            style={{ borderColor: "rgba(255,255,255,0.1)" }}
          >
            <p>© {new Date().getFullYear()} BeeToo Agency S.r.l. · Tutti i diritti riservati</p>
            <span className="hidden md:inline opacity-30">•</span>
            <a
              href="mailto:info@beetooagency.com"
              className="hover:underline underline-offset-4"
              style={{ color: "#ffffff" }}
            >
              info@beetooagency.com
            </a>
            <span className="hidden md:inline opacity-30">•</span>
            <a
              href="/login"
              data-testid="footer-login-link"
              className="hover:underline underline-offset-4"
              style={{ color: "#ffffff" }}
            >
              Accedi
            </a>
            <span className="hidden md:inline opacity-30">•</span>
            <a
              href="/privacy-policy"
              className="hover:underline underline-offset-4"
              style={{ color: "#ffffff" }}
            >
              Privacy Policy
            </a>
            <span className="hidden md:inline opacity-30">•</span>
            <a
              href="/cookie-policy"
              className="hover:underline underline-offset-4"
              style={{ color: "#ffffff" }}
            >
              Cookie Policy
            </a>
          </div>
        </Reading>
      </Section>

      <LeadDialog open={open} onOpenChange={setOpen} />
    </main>
  );
}
